import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, ChevronDown, Loader2, Check, Search, ShoppingCart, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/logo.png';
import NotificationBell from '@/components/NotificationBell';
import { supabase } from '@/integrations/supabase/client';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { Geolocation } from '@capacitor/geolocation';

interface HeaderProps {
  onCityDetected?: (city: string) => void;
}

interface CityRecord { id: string; name: string; center_lat?: number; center_lng?: number; radius_km?: number; }

const Header = ({ onCityDetected }: HeaderProps) => {
  const [city, setCity] = useState('Detecting...');
  const [locating, setLocating] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { totalItems } = useCart();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch active cities using React Query (syncs with Index.tsx cache)
  const { data: cityRecords = [] } = useQuery({
    queryKey: ['activeCities'],
    queryFn: async () => {
      const { data } = await supabase.from('cities').select('id, name, center_lat, center_lng, radius_km').eq('is_active', true).order('name');
      return (data || []) as CityRecord[];
    },
    staleTime: 60 * 60 * 1000, // 1 hour cache
  });

  const cities = useMemo(() => cityRecords.map(c => c.name), [cityRecords]);

  // Haversine formula for precise radius geofencing
  const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Match detected location to a city in our DB
  const matchCityFromAddress = (addressData: any): string | null => {
    if (!cityRecords.length) return null;
    // Try matching against all address fields
    const fieldsToCheck = [
      addressData?.state_district, addressData?.county,
      addressData?.city, addressData?.town,
      addressData?.village, addressData?.suburb, addressData?.neighbourhood,
      addressData?.state,
    ].filter(Boolean);

    for (const field of fieldsToCheck) {
      const match = cityRecords.find(c => c.name.toLowerCase() === field.toLowerCase());
      if (match) return match.id;
    }
    return null;
  };

  const { user } = useAuth();
  const [hasPrompted, setHasPrompted] = useState(false);

  // Initialize from cache
  useEffect(() => {
    const cachedDisplay = sessionStorage.getItem('detected_city_display');
    const cachedFull = sessionStorage.getItem('detected_city');
    if (cachedDisplay || cachedFull) {
      setCity(cachedDisplay || cachedFull || 'India');
      setLocating(false);
      onCityDetected?.(cachedFull || cachedDisplay || 'India');
    } else {
      setCity('India');
      setLocating(false);
      onCityDetected?.('India');
    }
  }, []);

  // Auto-detect for EVERYONE (guests + logged-in users)
  // BUT: If the user has ALREADY manually selected a city, do NOT auto-detect
  useEffect(() => {
    if (sessionStorage.getItem('manual_city_selection')) return; // ⚡ Overridden by user choice

    // Add a 4-hour TTL to the location cache so travelers don't get stuck with stale city data
    const lastDetected = sessionStorage.getItem('location_detected_time');
    const now = new Date().getTime();
    const isExpired = lastDetected && (now - parseInt(lastDetected)) > 4 * 60 * 60 * 1000;

    if (!isExpired && (locating || hasPrompted || sessionStorage.getItem('location_detected'))) return;
    
    const timer = setTimeout(() => {
      handleRedetect(true); 
      setHasPrompted(true);
      sessionStorage.setItem('location_detected', 'true');
      sessionStorage.setItem('location_detected_time', now.toString());
    }, 1200);
    
    return () => clearTimeout(timer);
  }, [user, locating, hasPrompted]); 

  const applyCity = (name: string, isServiceable: boolean = true, fullName?: string) => {
    // For unserviceable areas, 'name' is the district (display), 'fullName' is specific place
    const displayName = name;
    const storageName = fullName || name;

    setCity(displayName);
    setLocating(false);
    sessionStorage.setItem('detected_city', storageName);
    sessionStorage.setItem('detected_city_display', displayName);
    sessionStorage.setItem('is_unserviceable', isServiceable ? 'false' : 'true');
    
    const match = cityRecords.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (match) {
      sessionStorage.setItem('detected_city_id', match.id);
    } else {
      sessionStorage.removeItem('detected_city_id');
    }
    onCityDetected?.(storageName);
  };

  const handleSelectCity = (name: string) => {
    console.debug('Header: Manual city selection', { name });
    sessionStorage.setItem('manual_city_selection', 'true'); // ⚡ Set manual override
    applyCity(name, true);
    setDropdownOpen(false);
    setSearchTerm('');
  };

  const handleRedetect = (isAuto = false) => {
    setLocating(true);
    setCity('Detecting...');
    if (!isAuto) setDropdownOpen(false);
    setSearchTerm('');

    const detectViaIP = () => {
      applyCity('India');
    };

    if (!navigator.geolocation) {
      detectViaIP();
      return;
    }

    const getLocation = async () => {
      try {
        // Request permissions first on Native
        const permission = await Geolocation.checkPermissions();
        if (permission.location !== 'granted') {
           const request = await Geolocation.requestPermissions();
           if (request.location !== 'granted') {
             detectViaIP();
             return;
           }
        }

        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 5000 });
        const { latitude, longitude } = pos.coords;
        
        // 1. Try Geometric Radius matching FIRST for maximum accuracy
        let matchedCity = cityRecords.find(c => {
          if (c.center_lat && c.center_lng && c.radius_km) {
            const distance = getDistance(latitude, longitude, Number(c.center_lat), Number(c.center_lng));
            return distance <= Number(c.radius_km);
          }
          return false;
        });

        // 2. Fallback to reverse geocoding
        if (!matchedCity) {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&email=help.fixeveryday@gmail.com`
          );
          const data = await res.json();
          const addr = data.address || {};
          const matchedId = matchCityFromAddress(addr);
          
          if (matchedId) {
            matchedCity = cityRecords.find(c => c.id === matchedId);
          }

          if (!matchedCity) {
            const place = addr.village || addr.suburb || addr.neighbourhood || addr.hamlet || addr.locality || addr['sub-city'] || addr.city_district || addr.quarter || addr.town || addr.city || addr.allotments || addr.croft || addr.isolated_dwelling || addr.residential || '';
            const district = addr.state_district || addr.county || addr.state || 'India';
            
            const combinedLocation = place && place !== district 
              ? `${place}, ${district}` 
              : district;

            applyCity(district, false, combinedLocation);
            return;
          }
        }

        if (matchedCity) {
          applyCity(matchedCity.name, true);
        }

      } catch (err) {
        console.error('Geo error:', err);
        detectViaIP();
      }
    };

    getLocation();
  };

  const filteredCities = searchTerm.trim()
    ? cities.filter(c => c.toLowerCase().includes(searchTerm.toLowerCase()))
    : cities;

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="sticky top-0 z-50 glass-strong border-b"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-5xl mx-auto flex items-center justify-between h-14 md:h-16 px-4 md:px-6">
        <div className="flex items-center gap-2.5 md:gap-3">
          <motion.img
            src={logo}
            alt="FixEveryday"
            className="h-9 w-9 md:h-10 md:w-10 rounded-full shadow-sm ring-2 ring-primary/10"
            whileTap={{ scale: 0.9 }}
          />
          <div className="relative" ref={dropdownRef}>
            <h1 className="text-sm md:text-base font-extrabold italic text-[#FF0000] leading-none tracking-tight">FixEveryday</h1>
            <button
              onClick={() => !locating && setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-0.5 text-[11px] md:text-xs text-muted-foreground hover:text-primary transition-colors mt-0.5"
            >
              {locating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <MapPin className="h-3 w-3" />
              )}
              <span className="max-w-[120px] truncate">{city}</span>
              <ChevronDown className={`h-2.5 w-2.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full mt-2 w-56 bg-card rounded-2xl shadow-elevated border border-border/50 overflow-hidden z-50"
                >
                  {/* Search */}
                  <div className="p-2 border-b border-border/50">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <input
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Search city..."
                        autoFocus
                        className="w-full h-8 pl-8 pr-3 rounded-xl bg-secondary border-none text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/20"
                      />
                    </div>
                  </div>

                  {/* Re-detect option */}
                  <button
                    onClick={() => handleRedetect(false)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-primary hover:bg-secondary transition-colors"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    Use current location
                  </button>

                  {/* City list */}
                  <div className="max-h-48 overflow-y-auto">
                    {filteredCities.length > 0 ? (
                      filteredCities.map(c => (
                        <button
                          key={c}
                          onClick={() => handleSelectCity(c)}
                          className="w-full flex items-center justify-between px-3 py-2.5 text-xs hover:bg-secondary transition-colors"
                        >
                          <span className="font-medium">{c}</span>
                          {city === c && <Check className="h-3.5 w-3.5 text-primary" />}
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-3 text-[11px] text-muted-foreground text-center">No cities found</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <Link to="/store">
            <motion.div
              whileTap={{ scale: 0.9 }}
              className="p-2.5 rounded-xl hover:bg-secondary transition-colors relative"
            >
              <ShoppingBag className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
            </motion.div>
          </Link>
          <Link to="/cart">
            <motion.div
              whileTap={{ scale: 0.9 }}
              className="p-2.5 rounded-xl hover:bg-secondary transition-colors relative"
            >
              <ShoppingCart className="h-5 w-5 text-muted-foreground" />
              {totalItems > 0 && (
                <span className="absolute top-1.5 right-1.5 h-4 min-w-[16px] px-1 bg-primary text-[10px] font-bold text-primary-foreground rounded-full flex items-center justify-center shadow-sm">
                  {totalItems}
                </span>
              )}
            </motion.div>
          </Link>
          <NotificationBell />
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
